import { useState } from "react";
import { motion } from "framer-motion";
import { Send, Check, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

const ContactForm = () => {
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [status, setStatus] = useState<"idle" | "sending" | "sent" | "error">("idle");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !message.trim()) return;

    setStatus("sending");
    try {
      // Store in database
      const { error: dbError } = await supabase
        .from("contact_submissions")
        .insert({ email: email.trim(), message: message.trim() });

      if (dbError) throw dbError;

      // Send notification email via edge function
      const { error: fnError } = await supabase.functions.invoke("contact-notify", {
        body: { email: email.trim(), message: message.trim() },
      });

      // Even if email fails, submission is saved
      if (fnError) console.warn("Email notification failed, but submission saved.");

      setStatus("sent");
      setEmail("");
      setMessage("");
      setTimeout(() => setStatus("idle"), 4000);
    } catch {
      setStatus("error");
      setTimeout(() => setStatus("idle"), 3000);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 w-full">
      <div>
        <input
          type="email"
          required
          maxLength={255}
          placeholder="Work email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={status === "sending" || status === "sent"}
          className="w-full bg-transparent border border-white/10 rounded-lg px-4 py-3 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-accent transition-colors duration-medium font-mono"
        />
      </div>
      <div>
        <textarea
          required
          maxLength={1000}
          rows={3}
          placeholder="What are you trying to build, fix or rethink?"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          disabled={status === "sending" || status === "sent"}
          className="w-full bg-transparent border border-white/10 rounded-lg px-4 py-3 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-accent transition-colors duration-medium font-mono resize-none"
        />
      </div>
      <motion.button
        type="submit"
        disabled={status === "sending" || status === "sent"}
        className="flex items-center gap-2 px-5 py-3 bg-accent text-accent-foreground font-mono text-xs uppercase tracking-widest rounded-lg hover:bg-accent/90 transition-colors duration-medium disabled:opacity-50 min-h-[44px]"
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
      >
        {status === "sending" && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
        {status === "sent" && <Check className="w-3.5 h-3.5" />}
        {status === "idle" && <Send className="w-3.5 h-3.5" />}
        {status === "error" && <Send className="w-3.5 h-3.5" />}
        {status === "sending" ? "Sending…" : status === "sent" ? "Sent" : status === "error" ? "Failed, retry" : "Send"}
      </motion.button>
    </form>
  );
};

export default ContactForm;
